import React from "react";
import {
  Box,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Skeleton,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import {
  Dashboard,
  Settings as SettingsIcon,
  IntegrationInstructions,
  LocalOffer,
  Draw,
  PersonAddOutlined,
  Assignment,
  ListAlt,
  Timeline,
  History,
  ExpandLess,
  ExpandMore,
  PlayArrow,
  CheckCircleOutline,
  Feedback as FeedbackIcon,
  BugReportOutlined,
  MenuOpen,
  Menu as MenuIcon,
} from "@mui/icons-material";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  selectBoardState,
  selectSelectedItem,
} from "@/features/bughurd/boardSelector";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import {
  Drawer,
  DrawerConfig,
  getSelectedItemFromPath,
  getPathFromSelectedItem,
  getSprintIdFromPath,
} from "../utils";

import {
  resetBoardState,
  setBacklogViewType,
  setSelectedItem,
  setStatusList,
  setViewType,
} from "@/features/bughurd/slice";
import RoomPreferencesOutlinedIcon from "@mui/icons-material/RoomPreferencesOutlined";
import { useGetSprintListQuery } from "@/features/bughurd/api";
import SnifferIcon from "@/assets/icons/sniffer-loader.svg?react";

type SprintStatus = "DRAFT" | "ACTIVE" | "IN_ACTIVE" | "ARCHIVE";

type Sprint = {
  id: string | number;
  name: string;
  status: SprintStatus;
  route?: string;
  sprintData?: any;
};

interface BoardSidebarProps {
  onItemSelect?: (item: string, sprintData?: any) => void; // Deprecated: Use route-based navigation
  onDrawerToggle: () => void;
  isCurrentUserClient?: boolean;
}

// Helper function to get sprint status chip props
const getSprintStatusProps = (status: SprintStatus) => {
  switch (status) {
    case "ACTIVE":
      return {
        label: "Active",
        color: "success" as const,
        variant: "filled" as const,
        icon: undefined,
        sx: {},
      };
    case "ARCHIVE":
      return {
        label: "Completed",
        color: "default" as const,
        variant: "outlined" as const,
        icon: <CheckCircleOutline sx={{ fontSize: "0.875rem" }} />,
        sx: {
          borderColor: "#4caf50",
          color: "#4caf50",
          backgroundColor: "rgba(76, 175, 80, 0.08)",
          "& .MuiChip-icon": {
            color: "#4caf50",
          },
        },
      };
    case "DRAFT":
    case "IN_ACTIVE":
      return {
        label: status === "DRAFT" ? "Draft" : "Inactive",
        color: "default" as const,
        variant: "outlined" as const,
        icon: undefined,
        sx: {},
      };
    default:
      return {
        label: status,
        color: "default" as const,
        variant: "outlined" as const,
        icon: undefined,
        sx: {},
      };
  }
};

const BoardSidebar: React.FC<BoardSidebarProps> = ({
  onDrawerToggle,
  isCurrentUserClient = false,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();

  const baseUrl = window?.location?.origin;
  const isPmoHub = baseUrl?.includes("pmohub") || false;
  const isSniffer =
    baseUrl?.includes("sniffer") || baseUrl?.includes("pmohub") || false;

  const boardState = useAppSelector(selectBoardState);
  const reduxSelectedItem = useAppSelector(selectSelectedItem);

  // Determine selected item from URL (primary) or Redux (fallback)
  const selectedItemFromUrl = getSelectedItemFromPath(pathname);
  const sprintIdFromUrl = getSprintIdFromPath(pathname);
  const selectedItem = selectedItemFromUrl || reduxSelectedItem;

  const isMyTaskView =
    selectedItem === "My Tasks" ||
    pathname.includes("/defect-tracker/my-tasks");

  // Helper function to handle navigation
  const handleNavigation = (item: string, sprintId?: string) => {
    if (!id && item !== "My Tasks") return;
    const path = getPathFromSelectedItem(item, id || "", sprintId);
    navigate(path);
  };

  const { drawerOpen, projectDetails } = boardState;
  const boardType = projectDetails?.boardType;
  const preference = projectDetails?.preference;

  const [openMap, setOpenMap] = React.useState<Record<string, boolean>>({
    Settings: false,
    Sprints: false,
  });

  const [sprints, setSprints] = React.useState<Sprint[] | null>(null);
  const [sprintsLoading, setSprintsLoading] = React.useState(false);
  const [sprintsLoadedOnce, setSprintsLoadedOnce] = React.useState(false);

  const {
    data: sprintListData,
    isLoading: isSprintLoading,
    refetch: refetchSprints,
    isFetching: isSprintFetching,
  } = useGetSprintListQuery(
    {
      projectId: id || "",
      page: 1,
      limit: 100,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    },
  );

  const apiSprints = React.useMemo(
    () => sprintListData?.data?.decryptedData?.items || [],
    [sprintListData],
  );

  // Format sprint display name
  const formatSprintName = (s: any) => {
    return s?.name ?? `Sprint ${s?.sprintNumber ?? ""}`.trim();
  };

  const hydrateSprints = React.useCallback((): Sprint[] => {
    const allSprintItem: Sprint = {
      id: "all",
      name: t("All Sprints"),
      status: "ACTIVE" as const,
    };

    const mappedSprints: Sprint[] = [];
    for (const s of apiSprints) {
      mappedSprints.push({
        id: s?._id ?? s?.id ?? `${s?.sprintKey ?? s?.name}`,
        name: formatSprintName(s),
        status: (s?.status as SprintStatus) || "DRAFT",
        sprintData: s,
      });
    }

    const statusPriority: Record<SprintStatus, number> = {
      ACTIVE: 1,
      DRAFT: 2,
      ARCHIVE: 3,
      IN_ACTIVE: 4,
    };

    mappedSprints.sort((a, b) => {
      const priorityA = statusPriority[a.status] ?? 99;
      const priorityB = statusPriority[b.status] ?? 99;
      return priorityA - priorityB;
    });

    return [allSprintItem, ...mappedSprints];
  }, [apiSprints]);

  const toggleGroup = async (key: string) => {
    if (key === "Sprints" && !openMap.Sprints && !sprintsLoadedOnce) {
      setSprintsLoading(true);
      try {
        refetchSprints();
        setSprints(hydrateSprints());
        setSprintsLoadedOnce(true);
      } finally {
        setSprintsLoading(false);
      }
    }
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Update sprints when API data changes
  React.useEffect(() => {
    if (sprintsLoadedOnce && apiSprints.length >= 0) {
      setSprints(hydrateSprints());
    }
  }, [apiSprints, sprintsLoadedOnce, hydrateSprints]);
  
  const isSupport = projectDetails?.configurationType === "SUPPORT" || false;  

  const primaryItems = isMyTaskView
    ? []
    : boardType === "SCRUM"
      ? [{ text: "Backlog", icon: <ListAlt />, miniName: "Backlog" }]
      : [
          {
            text: "Task Board",
            icon: <AssignmentOutlinedIcon />,
            miniName: "Tasks",
          },
        ];

  const settingsChildren = [
    {
      text: "Project Settings",
      icon: <RoomPreferencesOutlinedIcon />,
      miniName: "Project",
    },
    { text: "Members", icon: <PersonAddOutlined />, miniName: "Members" },
    ...(preference?.isBoard !== false
      ? [{ text: "Tags", icon: <LocalOffer />, miniName: "Tags" }]
      : []),
    {
      text: "Integrations",
      icon: <IntegrationInstructions />,
      miniName: "Integrations",
    },
    ...(preference?.isBoard !== false
      ? [{ text: "Appearance", icon: <Draw />, miniName: "Appearance" }]
      : []),
    ...(isSupport && !isPmoHub && preference?.isLiveLog !== false
      ? [
          {
            text: "Live Logs Docs",
            icon: <BugReportOutlined />,
            miniName: "Autobug",
          },
        ]
      : []),
  ];

  const renderListItem = (
    label: string,
    icon: React.ReactNode,
    miniName: string,
    depth = 0,
  ) => {
    const isSelected = selectedItem === label || (label === "Feedback" && selectedItem === "Feedback Config");

    return (
      <ListItem
        disablePadding
        sx={{ display: "block", position: "relative" }}
        key={`${label}-${depth}`}
      >
        {/* Active indicator bar */}
        {isSelected && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: 3,
              height: "60%",
              borderRadius: "0 4px 4px 0",
              backgroundColor: (theme) => theme.palette.primary.main,
              transition: "all 0.2s ease",
            }}
          />
        )}
        <ListItemButton
          onClick={() => handleNavigation(label)}
          sx={{
            minHeight: 44,
            mx: drawerOpen ? 1 : 0.5,
            my: 0.25,
            borderRadius: drawerOpen ? "10px" : "12px",
            pl: drawerOpen ? (depth > 0 ? 2 + depth * 1.5 : 2) : 0,
            pr: drawerOpen ? 2 : 0,
            justifyContent: drawerOpen ? "flex-start" : "center",
            transition: "all 0.2s ease",
            backgroundColor: (theme) =>
              isSelected
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
            "&:hover": {
              backgroundColor: (theme) =>
                isSelected
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.action.hover, 0.06),
            },
          }}
        >
          {drawerOpen ? (
            <>
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: (theme) =>
                    isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.3rem",
                  },
                }}
              >
                {icon}
              </ListItemIcon>
              <ListItemText
                primary={t(label)}
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "0.875rem",
                    fontWeight: isSelected ? 600 : 400,
                    color: (theme) =>
                      isSelected
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                  },
                }}
              />
            </>
          ) : (
            <Tooltip title={t(label)} placement="right" arrow>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  py: 0.25,
                  color: (theme) =>
                    isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.3rem",
                  },
                }}
              >
                {icon}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: isSelected ? 600 : 400,
                    textAlign: "center",
                    lineHeight: 1.2,
                    mt: 0.25,
                    maxWidth: "52px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "inherit",
                  }}
                >
                  {t(miniName)}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSprintItem = (s: Sprint, depth = 1) => {
    // For sprint items, check if this sprint is selected based on URL sprint ID
    const isSelected =
      s.id === "all"
        ? selectedItem === "All Sprints"
        : sprintIdFromUrl === String(s.id) ||
          selectedItem === s.name ||
          selectedItem === `Sprint:${s.id}`;
    const statusProps = s.id !== "all" ? getSprintStatusProps(s.status) : null;

    return (
      <ListItem
        disablePadding
        sx={{ display: "block", position: "relative" }}
        key={`sprint-${s.id}`}
      >
        {isSelected && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: 3,
              height: "60%",
              borderRadius: "0 4px 4px 0",
              backgroundColor: (theme) => theme.palette.primary.main,
            }}
          />
        )}
        <ListItemButton
          onClick={() => {
            if (s.id === "all") {
              handleNavigation("All Sprints");
            } else {
              const sprintId = String(s.id);
              handleNavigation(s.name, sprintId);
            }
          }}
          sx={{
            minHeight: 40,
            mx: drawerOpen ? 1 : 0.5,
            my: 0.25,
            borderRadius: drawerOpen ? "10px" : "12px",
            pl: drawerOpen ? 2 + depth * 1 : 0,
            pr: drawerOpen ? 1.5 : 0,
            justifyContent: drawerOpen ? "flex-start" : "center",
            transition: "all 0.2s ease",
            backgroundColor: (theme) =>
              isSelected
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
            "&:hover": {
              backgroundColor: (theme) =>
                isSelected
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.action.hover, 0.06),
            },
          }}
        >
          {drawerOpen ? (
            <>
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  color: (theme) =>
                    isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.1rem",
                  },
                }}
              >
                <PlayArrow />
              </ListItemIcon>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <ListItemText
                  primary={s.name}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    "& .MuiTypography-root": {
                      color: (theme) =>
                        isSelected
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      fontSize: "0.8125rem",
                      fontWeight: isSelected ? 600 : 400,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
                {statusProps && (
                  <Chip
                    label={statusProps.label}
                    size="small"
                    color={statusProps.color}
                    variant={statusProps.variant}
                    icon={statusProps.icon}
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      flexShrink: 0,
                      "& .MuiChip-label": {
                        px: 0.75,
                      },
                      ...statusProps.sx,
                    }}
                  />
                )}
              </Box>
            </>
          ) : (
            <Tooltip title={s.name} placement="right" arrow>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  py: 0.25,
                  color: (theme) =>
                    isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.2rem",
                  },
                }}
              >
                <PlayArrow />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.55rem",
                    fontWeight: isSelected ? 600 : 400,
                    textAlign: "center",
                    lineHeight: 1.2,
                    mt: 0.25,
                    maxWidth: "52px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "inherit",
                  }}
                >
                  {s.id === "all" ? "All" : "Sprint"}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const SprintSkeletonRow = ({ depth = 1 }: { depth?: number }) => (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton
        disableRipple
        sx={{
          minHeight: 40,
          mx: drawerOpen ? 1 : 0.5,
          my: 0.25,
          borderRadius: drawerOpen ? "10px" : "12px",
          pl: drawerOpen ? 2 + depth * 1 : 0,
          pr: drawerOpen ? 1.5 : 0,
          justifyContent: drawerOpen ? "flex-start" : "center",
        }}
      >
        {drawerOpen ? (
          <>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Skeleton variant="circular" width={20} height={20} />
            </ListItemIcon>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={20} />
              </Box>
              <Skeleton
                variant="rectangular"
                width={50}
                height={20}
                sx={{ borderRadius: "10px" }}
              />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={30} height={12} />
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );

  const shouldShowSkeleton =
    openMap.Sprints &&
    (isSprintLoading ||
      isSprintFetching ||
      sprintsLoading ||
      !sprintsLoadedOnce ||
      sprints === null);

  const showEmptyState =
    !shouldShowSkeleton && sprintsLoadedOnce && (sprints?.length ?? 0) <= 1;

  return (
    <Drawer
      variant="permanent"
      open={drawerOpen}
      sx={{
        "& .MuiDrawer-paper": {
          ...DrawerConfig,
          overflowX: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: drawerOpen ? "space-between" : "center",
          minHeight: "53px",
          px: drawerOpen ? 1.5 : 0,
          flexShrink: 0,
        }}
      >
        {drawerOpen && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 0.5,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& svg": { width: "100%", height: "100%" },
              }}
            >
              <SnifferIcon />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                color: (theme) => theme.palette.primary.main,
                letterSpacing: "0.02em",
                lineHeight: 1,
              }}
            >
              Sniffer
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={onDrawerToggle}
          size="small"
          sx={{
            color: (theme) => theme.palette.text.secondary,
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              color: (theme) => theme.palette.primary.main,
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {drawerOpen ? (
            <MenuOpen fontSize="small" />
          ) : (
            <MenuIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <Divider />
      <Box
        sx={{
          height: "calc(100% - 120px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ overflow: "auto", minHeight: 0, flex: 1 }}>
          <List>
            {/* For CLIENT users, only show Feedback tab */}
            {isCurrentUserClient ? (
              <>
                {!isMyTaskView &&
                  !isPmoHub &&
                  isSupport &&
                  preference?.isFeedback !== false &&
                  renderListItem("Feedback", <FeedbackIcon />, "Feedback", 0)}
              </>
            ) : (
              <>
                {!isMyTaskView &&
                  preference?.isBoard !== false &&
                  renderListItem("Dashboard", <Dashboard />, "Dashboard", 0)}
                {!isMyTaskView &&
                  !isPmoHub &&
                  isSupport &&
                  preference?.isFeedback !== false &&
                  renderListItem("Feedback", <FeedbackIcon />, "Feedback", 0)}
                {preference?.isBoard !== false &&
                  primaryItems.map(({ text, icon, miniName }) =>
                    renderListItem(text, icon, miniName, 0),
                  )}
                {!isMyTaskView &&
                  !isPmoHub &&
                  renderListItem(
                    "Applications",
                    <AppsOutlinedIcon />,
                    "Apps",
                    0,
                  )}
                {!isMyTaskView &&
                  !isPmoHub &&
                  isSupport &&
                  preference?.isLiveLog !== false &&
                  renderListItem(
                    "Live Logs",
                    <BugReportOutlined />,
                    "Live Logs",
                    0,
                  )}

                {boardType === "SCRUM" &&
                  !isMyTaskView &&
                  preference?.isBoard !== false && (
                    <ListItem
                      disablePadding
                      sx={{ display: "block" }}
                      key="Sprints-parent"
                    >
                      <ListItemButton
                        onClick={() => toggleGroup("Sprints")}
                        sx={{
                          minHeight: 44,
                          mx: drawerOpen ? 1 : 0.5,
                          my: 0.25,
                          borderRadius: drawerOpen ? "10px" : "12px",
                          pl: drawerOpen ? 2 : 0,
                          pr: drawerOpen ? 1.5 : 0,
                          justifyContent: drawerOpen ? "flex-start" : "center",
                          transition: "all 0.2s ease",
                          backgroundColor: (theme) =>
                            openMap.Sprints
                              ? alpha(theme.palette.primary.main, 0.06)
                              : "transparent",
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.action.hover, 0.06),
                          },
                        }}
                      >
                        {drawerOpen ? (
                          <>
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: (theme) =>
                                  openMap.Sprints
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                "& .MuiSvgIcon-root": {
                                  fontSize: "1.3rem",
                                },
                              }}
                            >
                              <Timeline />
                            </ListItemIcon>
                            <ListItemText
                              primary={t("Sprints")}
                              sx={{
                                "& .MuiTypography-root": {
                                  fontSize: "0.875rem",
                                  fontWeight: openMap.Sprints ? 600 : 400,
                                  color: (theme) =>
                                    openMap.Sprints
                                      ? theme.palette.primary.main
                                      : theme.palette.text.primary,
                                },
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {(isSprintLoading || isSprintFetching) && (
                                <CircularProgress
                                  size={14}
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.primary.main,
                                  }}
                                />
                              )}
                              {openMap.Sprints ? (
                                <ExpandLess
                                  sx={{
                                    fontSize: "1.2rem",
                                    color: "text.secondary",
                                  }}
                                />
                              ) : (
                                <ExpandMore
                                  sx={{
                                    fontSize: "1.2rem",
                                    color: "text.secondary",
                                  }}
                                />
                              )}
                            </Box>
                          </>
                        ) : (
                          <Tooltip title={t("Sprints")} placement="right" arrow>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                py: 0.25,
                                color: (theme) =>
                                  openMap.Sprints
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                "& .MuiSvgIcon-root": {
                                  fontSize: "1.3rem",
                                },
                              }}
                            >
                              <Timeline />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.6rem",
                                  fontWeight: openMap.Sprints ? 600 : 400,
                                  textAlign: "center",
                                  lineHeight: 1.2,
                                  mt: 0.25,
                                  color: "inherit",
                                }}
                              >
                                {t("Sprints")}
                              </Typography>
                              <ExpandCircleDownOutlinedIcon
                                sx={{
                                  fontSize: "0.85rem",
                                  mt: 0.25,
                                  transition: "transform 0.2s ease",
                                  transform: openMap.Sprints
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                  color: "inherit",
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </ListItemButton>

                      <Collapse
                        in={openMap.Sprints}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            maxHeight: drawerOpen ? "280px" : "none",
                            overflowY: drawerOpen ? "auto" : "visible",
                            overflowX: "hidden",
                            "&::-webkit-scrollbar": {
                              width: "4px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              borderRadius: "4px",
                              backgroundColor: (theme) =>
                                alpha(theme.palette.text.primary, 0.15),
                            },
                          }}
                        >
                          <List disablePadding>
                            {shouldShowSkeleton &&
                              Array.from({ length: 5 }).map((_, idx) => (
                                <SprintSkeletonRow
                                  key={`sprint-skel-${idx}`}
                                  depth={1}
                                />
                              ))}

                            {!shouldShowSkeleton &&
                              (sprints?.length ?? 0) > 0 &&
                              sprints?.map((s) => renderSprintItem(s, 1))}

                            {showEmptyState && (
                              <ListItem
                                disablePadding
                                sx={{ display: "block" }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: 2,
                                    px: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {t("No sprints found")}
                                  </Typography>
                                </Box>
                              </ListItem>
                            )}
                          </List>
                        </Box>
                      </Collapse>
                    </ListItem>
                  )}

                {!isMyTaskView && (
                  <ListItem
                    disablePadding
                    sx={{ display: "block" }}
                    key="Settings-parent"
                  >
                    <ListItemButton
                      onClick={() => toggleGroup("Settings")}
                      sx={{
                        minHeight: 44,
                        mx: drawerOpen ? 1 : 0.5,
                        my: 0.25,
                        borderRadius: drawerOpen ? "10px" : "12px",
                        pl: drawerOpen ? 2 : 0,
                        pr: drawerOpen ? 1.5 : 0,
                        justifyContent: drawerOpen ? "flex-start" : "center",
                        transition: "all 0.2s ease",
                        backgroundColor: (theme) =>
                          openMap.Settings
                            ? alpha(theme.palette.primary.main, 0.06)
                            : "transparent",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            alpha(theme.palette.action.hover, 0.06),
                        },
                      }}
                    >
                      {drawerOpen ? (
                        <>
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: (theme) =>
                                openMap.Settings
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              "& .MuiSvgIcon-root": {
                                fontSize: "1.3rem",
                              },
                            }}
                          >
                            <SettingsIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={t("Settings")}
                            sx={{
                              "& .MuiTypography-root": {
                                fontSize: "0.875rem",
                                fontWeight: openMap.Settings ? 600 : 400,
                                color: (theme) =>
                                  openMap.Settings
                                    ? theme.palette.primary.main
                                    : theme.palette.text.primary,
                              },
                            }}
                          />
                          {openMap.Settings ? (
                            <ExpandLess
                              sx={{
                                fontSize: "1.2rem",
                                color: "text.secondary",
                              }}
                            />
                          ) : (
                            <ExpandMore
                              sx={{
                                fontSize: "1.2rem",
                                color: "text.secondary",
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <Tooltip title={t("Settings")} placement="right" arrow>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              py: 0.25,
                              color: (theme) =>
                                openMap.Settings
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              "& .MuiSvgIcon-root": {
                                fontSize: "1.3rem",
                              },
                            }}
                          >
                            <SettingsIcon />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                fontWeight: openMap.Settings ? 600 : 400,
                                textAlign: "center",
                                lineHeight: 1.2,
                                mt: 0.25,
                                color: "inherit",
                              }}
                            >
                              {t("Settings")}
                            </Typography>
                            <ExpandCircleDownOutlinedIcon
                              sx={{
                                fontSize: "0.85rem",
                                mt: 0.25,
                                transition: "transform 0.2s ease",
                                transform: openMap.Settings
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                color: "inherit",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </ListItemButton>

                    <Collapse
                      in={openMap.Settings}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          ...(!drawerOpen && {
                            mx: 0.5,
                            my: 0.5,
                            py: 0.5,
                            borderRadius: "12px",
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.06),
                            borderLeft: (theme) =>
                              `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                          }),
                        }}
                      >
                        <List disablePadding>
                          {settingsChildren.map(({ text, icon, miniName }) =>
                            renderListItem(text, icon, miniName, 1),
                          )}
                        </List>
                      </Box>
                    </Collapse>
                  </ListItem>
                )}

                {!isSniffer &&
                  !isMyTaskView &&
                  preference?.isBoard !== false &&
                  renderListItem(
                    "Recurring Tasks",
                    <LoopOutlinedIcon />,
                    "Recurring",
                    0,
                  )}

                {boardType === "SCRUM" &&
                  !isMyTaskView &&
                  preference?.isBoard !== false &&
                  renderListItem("Retrospectives", <History />, "Retros", 0)}
              </>
            )}
          </List>
        </Box>

        {/* Hide My Tasks section for CLIENT users */}
        {!isCurrentUserClient && !isSniffer && (
          <Box sx={{ flexShrink: 0, pb: 1 }}>
            <Divider sx={{ mx: 1.5, mb: 0.5 }} />
            <List disablePadding>
              <ListItem
                disablePadding
                sx={{ display: "block", position: "relative" }}
              >
                {isMyTaskView && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 3,
                      height: "60%",
                      borderRadius: "0 4px 4px 0",
                      backgroundColor: (theme) => theme.palette.primary.main,
                    }}
                  />
                )}
                <ListItemButton
                  onClick={() => {
                    if (isMyTaskView) return;
                    dispatch(resetBoardState());
                    dispatch(setStatusList([]));
                    dispatch(setSelectedItem("My Tasks"));
                    dispatch(setViewType("kanban"));
                    dispatch(setBacklogViewType("kanban"));
                    navigate("/defect-tracker/my-tasks");
                  }}
                  sx={{
                    minHeight: 44,
                    mx: drawerOpen ? 1 : 0.5,
                    my: 0.25,
                    borderRadius: drawerOpen ? "10px" : "12px",
                    pl: drawerOpen ? 2 : 0,
                    pr: drawerOpen ? 2 : 0,
                    justifyContent: drawerOpen ? "flex-start" : "center",
                    transition: "all 0.2s ease",
                    backgroundColor: (theme) =>
                      isMyTaskView
                        ? alpha(theme.palette.primary.main, 0.1)
                        : "transparent",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        isMyTaskView
                          ? alpha(theme.palette.primary.main, 0.15)
                          : alpha(theme.palette.action.hover, 0.06),
                    },
                  }}
                >
                  {drawerOpen ? (
                    <>
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: (theme) =>
                            isMyTaskView
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                          "& .MuiSvgIcon-root": {
                            fontSize: "1.3rem",
                          },
                        }}
                      >
                        <Assignment />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("My Tasks")}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "0.875rem",
                            fontWeight: isMyTaskView ? 600 : 400,
                            color: (theme) =>
                              isMyTaskView
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                          },
                        }}
                      />
                    </>
                  ) : (
                    <Tooltip title={t("My Tasks")} placement="right" arrow>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          py: 0.25,
                          color: (theme) =>
                            isMyTaskView
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                          "& .MuiSvgIcon-root": {
                            fontSize: "1.3rem",
                          },
                        }}
                      >
                        <Assignment />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: isMyTaskView ? 600 : 400,
                            textAlign: "center",
                            lineHeight: 1.2,
                            mt: 0.25,
                            color: "inherit",
                          }}
                        >
                          {t("My Tasks")}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default BoardSidebar;
